import portalApiClient from './portalApiClient';
import { extractApiError } from '../utils/apiError';
import {
  savePortalToken,
  getPortalToken,
  savePortalUser,
  getPortalUser,
  clearPortalData,
} from '../utils/portalStorage';

/**
 * Decode a JWT payload without verifying the signature (client-side only).
 * @returns {object|null}
 */
const decodeJwt = (token) => {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    // JWTs are base64url — convert to base64 and pad before atob.
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch (e) {
    return null;
  }
};

/** @returns {boolean} true if the stored portal token exists and is not expired. */
const isPortalTokenValid = () => {
  const token = getPortalToken();
  if (!token) return false;
  const decoded = decodeJwt(token);
  if (!decoded) return true; // opaque token — treat as valid until the server 401s
  if (!decoded.exp) return true;
  return decoded.exp * 1000 > Date.now();
};

const portalAuthService = {
  /**
   * Log in a customer with email OR mobile + password.
   * The backend normalizes the identifier (with/without country code/spaces).
   * @returns {{success:boolean, data?:object, error?:string, status?:number, locked?:boolean, rateLimited?:boolean}}
   */
  async login(identifier, password) {
    try {
      const res = await portalApiClient.post('/auth/login', { identifier, password });
      const token = res.data?.access_token;
      if (!token) {
        return { success: false, error: 'No token returned from server.' };
      }
      savePortalToken(token);

      // Fetch the profile so the shell has company_name / customer_type immediately.
      let profile = null;
      try {
        const me = await portalApiClient.get('/me');
        profile = me.data;
        savePortalUser(profile);
      } catch (e) {
        // Non-fatal: the shell can lazy-load /me later.
      }

      return { success: true, data: { token, user: profile } };
    } catch (error) {
      const status = error.response?.status;
      const message = extractApiError(error);
      // 401 with a lockout message means THIS account is temporarily frozen
      // (distinct from a plain bad-credentials 401). 429 = per-IP rate limit.
      const locked = status === 401 && /too many/i.test(message || '');
      const rateLimited = status === 429;
      return { success: false, error: message, status, locked, rateLimited };
    }
  },

  /** Log out the customer (best-effort server call) and clear local session. */
  async logout() {
    try {
      if (getPortalToken()) {
        await portalApiClient.post('/auth/logout');
      }
    } catch (e) {
      // ignore — clear locally regardless
    } finally {
      clearPortalData();
    }
    return { success: true };
  },

  /** GET /portal/me — the logged-in customer's profile. */
  async getProfile() {
    try {
      const res = await portalApiClient.get('/me');
      savePortalUser(res.data);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: extractApiError(error), status: error.response?.status };
    }
  },

  /**
   * Local check used on app boot: is there a usable portal session?
   * @returns {{isAuthenticated:boolean, user:object|null}}
   */
  checkPortalAuth() {
    if (!isPortalTokenValid()) {
      return { isAuthenticated: false, user: null };
    }
    return { isAuthenticated: true, user: getPortalUser() };
  },
};

export default portalAuthService;
